import { zodResolver } from '@hookform/resolvers/zod';
import {
  type BranchDetail,
  type BranchListItem,
  type CreateBranchRequest,
  CreateBranchRequestSchema,
  type UpdateBranchRequest,
  UpdateBranchRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect, useMemo } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';

import {
  useCityOptions,
  useCountryOptions,
  useCreateBranch,
  useStateOptions,
  useUpdateBranch,
} from '../use-branches';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';

interface BranchFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  branch?: BranchDetail | BranchListItem | null;
}

const CREATE_DEFAULTS: CreateBranchRequest = {
  code: '',
  name: '',
  isActive: true,
  sortOrder: 0,
};

/**
 * The field set is identical for create + edit; it reads the form via context
 * (UzFormField → useFormContext), so it renders correctly inside whichever
 * UzForm wraps it.
 *
 * Country → state → city form a dependent chain: selecting a country clears
 * the state and city fields; selecting a state re-fetches the city list and
 * clears city. This mirrors the CityFormDrawer pattern.
 */
const BranchFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  const { setValue } = useFormContext();

  const countryOptions = useCountryOptions();
  const countryId = useWatch({ name: 'countryId' }) as string | undefined;
  const stateId = useWatch({ name: 'stateId' }) as string | undefined;

  const statesQuery = useStateOptions(countryId ?? null);
  const citiesQuery = useCityOptions(countryId ?? null, stateId ?? null);

  const countrySelectOptions = useMemo(
    () =>
      (countryOptions.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
      })),
    [countryOptions.data],
  );

  const stateSelectOptions = useMemo(
    () => (statesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statesQuery.data],
  );

  const citySelectOptions = useMemo(
    () => (citiesQuery.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [citiesQuery.data],
  );

  const statePlaceholder = !countryId
    ? 'Select a country first'
    : statesQuery.isLoading
      ? 'Loading states…'
      : stateSelectOptions.length === 0
        ? 'No states found'
        : 'Select a state';

  const cityPlaceholder = !countryId
    ? 'Select a country first'
    : citiesQuery.isLoading
      ? 'Loading cities…'
      : citySelectOptions.length === 0
        ? 'No cities found'
        : 'Select a city';

  return (
    <>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="e.g. Pune HQ"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="countryId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Country"
            placeholder={countryOptions.isLoading ? 'Loading countries…' : 'Select a country'}
            options={countrySelectOptions}
            value={(field.value as string) || undefined}
            onValueChange={(v) => {
              field.onChange(v || null);
              // Clear downstream selections when the country changes.
              setValue('stateId', null, { shouldValidate: false });
              setValue('cityId', null, { shouldValidate: false });
            }}
            error={error}
            disabled={pending || countryOptions.isLoading}
          />
        )}
      </UzFormField>
      <UzFormField name="stateId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="State"
            placeholder={statePlaceholder}
            options={stateSelectOptions}
            value={(field.value as string) || undefined}
            onValueChange={(v) => {
              field.onChange(v || null);
              // Clear city when state changes.
              setValue('cityId', null, { shouldValidate: false });
            }}
            error={error}
            disabled={pending || !countryId || statesQuery.isLoading}
          />
        )}
      </UzFormField>
      <UzFormField name="cityId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="City"
            placeholder={cityPlaceholder}
            options={citySelectOptions}
            value={(field.value as string) || undefined}
            onValueChange={(v) => field.onChange(v || null)}
            error={error}
            disabled={pending || !countryId || citiesQuery.isLoading}
          />
        )}
      </UzFormField>
      <UzFormField name="gstin">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="GSTIN"
            placeholder="e.g. 27AAAAA0000A1Z5"
            hint="15-character GST registration number."
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="phone">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Phone"
            placeholder="+91-20-12345678"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="email">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            // Empty maps to null so the optional email validates when blank
            // (the schema rejects '' against the email() rule).
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Email"
            placeholder="branch@example.com"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="officeBadge">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Office badge"
            placeholder="e.g. Head Office"
            hint='Short tag shown on the website "Our locations" card.'
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="mapEmbedUrl">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Google Maps embed"
            rows={3}
            placeholder='Paste the <iframe …> from Google Maps → Share → "Embed a map" (or just its src URL)'
            hint="Drives the website map + the exact pin for this office."
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="addressLine1">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Address line 1"
            placeholder="Building / floor"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="addressLine2">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Address line 2"
            placeholder="Street / area (optional)"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="postalCode">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Postal code"
            placeholder="e.g. 411001"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Inactive branches are hidden from branch pickers."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

/** Coerce an empty string / undefined to null for optional text fields. */
const nullIfEmpty = (v: string | null | undefined): string | null => (v?.trim() ? v.trim() : null);

export const BranchFormDrawer = ({
  open,
  onOpenChange,
  mode,
  branch,
}: BranchFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createBranch = useCreateBranch();
  const updateBranch = useUpdateBranch();
  const pending = createBranch.isPending || updateBranch.isPending;

  const createForm = useForm<CreateBranchRequest>({
    resolver: zodResolver(CreateBranchRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateBranchRequest>({
    resolver: zodResolver(UpdateBranchRequestSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (branch) {
      // Prefill with resolved IDs from the list-item relation refs.
      const countryId = 'country' in branch && branch.country ? branch.country.id : undefined;
      const stateId = 'state' in branch && branch.state ? branch.state.id : undefined;
      const cityId = 'city' in branch && branch.city ? branch.city.id : undefined;
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: branch.name,
        countryId: countryId ?? null,
        stateId: stateId ?? null,
        cityId: cityId ?? null,
        gstin: 'gstin' in branch ? (branch.gstin ?? '') : '',
        phone: 'phone' in branch ? (branch.phone ?? '') : '',
        email: 'email' in branch ? (branch.email ?? null) : null,
        officeBadge: 'officeBadge' in branch ? (branch.officeBadge ?? '') : '',
        mapEmbedUrl: 'mapEmbedUrl' in branch ? (branch.mapEmbedUrl ?? '') : '',
        addressLine1: 'addressLine1' in branch ? ((branch as BranchDetail).addressLine1 ?? '') : '',
        addressLine2: 'addressLine2' in branch ? ((branch as BranchDetail).addressLine2 ?? '') : '',
        postalCode: 'postalCode' in branch ? ((branch as BranchDetail).postalCode ?? '') : '',
        sortOrder: branch.sortOrder,
        isActive: branch.isActive,
      });
    }
  }, [open, isCreate, branch, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field).
    await createBranch.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      gstin: nullIfEmpty(values.gstin),
      phone: nullIfEmpty(values.phone),
      email: nullIfEmpty(values.email),
      officeBadge: nullIfEmpty(values.officeBadge),
      mapEmbedUrl: nullIfEmpty(values.mapEmbedUrl),
      addressLine1: nullIfEmpty(values.addressLine1),
      addressLine2: nullIfEmpty(values.addressLine2),
      postalCode: nullIfEmpty(values.postalCode),
      countryId: values.countryId || null,
      stateId: values.stateId || null,
      cityId: values.cityId || null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!branch) return;
    await updateBranch.mutateAsync({
      id: branch.id,
      payload: {
        ...values,
        gstin: nullIfEmpty(values.gstin),
        phone: nullIfEmpty(values.phone),
        email: nullIfEmpty(values.email),
        officeBadge: nullIfEmpty(values.officeBadge),
        mapEmbedUrl: nullIfEmpty(values.mapEmbedUrl),
        addressLine1: nullIfEmpty(values.addressLine1),
        addressLine2: nullIfEmpty(values.addressLine2),
        postalCode: nullIfEmpty(values.postalCode),
        countryId: values.countryId || null,
        stateId: values.stateId || null,
        cityId: values.cityId || null,
      },
    });
    onOpenChange(false);
  });

  const fields = <BranchFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add branch' : `Edit ${branch?.name ?? 'branch'}`}
      description="Pick country → state → city to resolve the address. Code is the stable machine identifier."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
